import React from 'react';
import { useTelegram } from '../hooks/useTelegram';

const LS_KEY = 'japanexpress_profile_v1';
const LS_PHONE_KEY = 'japanexpress_phone_v1';
const LS_INN_KEY = 'japanexpress_inn_v1';
const LS_FULL_NAME_KEY = 'japanexpress_full_name_v1';
const LS_ADDRESS_KEY = 'japanexpress_address_v1';
const LS_NICKNAME_KEY = 'japanexpress_nickname_v1';
const LS_CUSTOMER_CODE_KEY = 'japanexpress_customer_code_v1';

const cyrillicOnly = (value) => String(value || '').replace(/[^а-яёА-ЯЁ\s\-]/g, '');

const readCachedFullName = () => {
  try {
    const raw = localStorage.getItem(LS_FULL_NAME_KEY);
    return String(raw || '').trim();
  } catch (_) {
    return '';
  }
};

const readCachedAddress = () => {
  try {
    const raw = localStorage.getItem(LS_ADDRESS_KEY);
    return String(raw || '').trim();
  } catch (_) {
    return '';
  }
};

const readCachedNickname = () => {
  try {
    const raw = localStorage.getItem(LS_NICKNAME_KEY);
    return String(raw || '').trim();
  } catch (_) {
    return '';
  }
};

const readCachedCustomerCode = () => {
  try {
    const raw = localStorage.getItem(LS_CUSTOMER_CODE_KEY);
    return String(raw || '').trim();
  } catch (_) {
    return '';
  }
};

const readCachedPhoneDigits = () => {
  try {
    const raw = localStorage.getItem(LS_PHONE_KEY);
    const digits = digitsOnly(raw).slice(0, 12);
    return digits;
  } catch (_) {
    return '';
  }
};

const readCachedInnDigits = () => {
  try {
    const raw = localStorage.getItem(LS_INN_KEY);
    const digits = digitsOnly(raw).slice(0, 14);
    return digits;
  } catch (_) {
    return '';
  }
};

const loadProfile = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

const saveProfile = (profile) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(profile));
  } catch (_) {
    // ignore
  }
};

const getApiPath = (path) => {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/api/')) return path;
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const res = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = data?.error || `Request failed: ${res.status}`;
    throw new Error(message);
  }
  return data;
};

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">{label}</div>
    <div className="mt-2">{children}</div>
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full rounded-2xl border border-stroke bg-[#161616] px-4 py-3 text-base text-white placeholder:text-white/45 outline-none focus:border-white/25"
    style={{ backgroundColor: '#161616', color: '#FFFFFF' }}
  />
);

const digitsOnly = (value) => (value || '').replace(/\D/g, '');

const formatPhoneInput = (value) => {
  const digits = digitsOnly(value).slice(0, 12);
  return `+${digits}`;
};

const maskPhone = (raw) => {
  const d = digitsOnly(raw).slice(0, 12);
  if (!d) return '';

  if (d.startsWith('7')) {
    const a = d.slice(0, 1);
    const b = d.slice(1, 4);
    const c = d.slice(4, 7);
    const e = d.slice(7, 9);
    const f = d.slice(9, 11);
    let out = `+${a}`;
    if (b) out += ` ${b}`;
    if (c) out += ` ${c}`;
    if (e) out += ` ${e}`;
    if (f) out += ` ${f}`;
    return out;
  }

  if (d.startsWith('996')) {
    const a = d.slice(0, 3);
    const b = d.slice(3, 6);
    const c = d.slice(6, 9);
    const e = d.slice(9, 12);
    let out = `+${a}`;
    if (b) out += ` ${b}`;
    if (c) out += ` ${c}`;
    if (e) out += ` ${e}`;
    return out;
  }

  if (d.startsWith('81')) {
    const a = d.slice(0, 2);
    const b = d.slice(2, 4);
    const c = d.slice(4, 8);
    const e = d.slice(8, 12);
    let out = `+${a}`;
    if (b) out += ` ${b}`;
    if (c) out += ` ${c}`;
    if (e) out += ` ${e}`;
    return out;
  }

  return `+${d}`;
};

const Toggle = ({ checked, onChange, label, description }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className="w-full text-left rounded-2xl border border-stroke bg-surface px-4 py-4 hover:bg-surface-hover transition"
  >
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="font-semibold text-text-primary">{label}</div>
        {description && <div className="mt-1 text-sm text-text-secondary">{description}</div>}
      </div>
      <div
        className={`h-6 w-11 rounded-full border transition flex items-center ${
          checked ? 'bg-neon-blue/20 border-neon-blue/40 justify-end' : 'bg-white/5 border-stroke justify-start'
        }`}
      >
        <div className={`h-5 w-5 rounded-full ${checked ? 'bg-neon-blue' : 'bg-white/35'}`} />
      </div>
    </div>
  </button>
);

const ProfilePage = () => {
  const { user, initData, tg } = useTelegram();
  const initial = React.useMemo(
    () =>
      (() => {
        const cachedDigits = readCachedPhoneDigits();
        const cachedPhone = cachedDigits ? maskPhone(cachedDigits) : '';
        const cachedInnDigits = readCachedInnDigits();
        const stored = loadProfile();
        return {
          fullName: readCachedFullName() || stored?.fullName || '',
          address: readCachedAddress() || stored?.address || '',
          nickname: readCachedNickname() || '',
          inn: cachedInnDigits,
          phone: cachedPhone,
          notifyStatus: typeof stored?.notifyStatus === 'boolean' ? stored.notifyStatus : true,
          notifyPromo: typeof stored?.notifyPromo === 'boolean' ? stored.notifyPromo : false,
        };
      })(),
    []
  );

  const [profile, setProfile] = React.useState(initial);
  const [savedAt, setSavedAt] = React.useState(null);
  const [error, setError] = React.useState('');
  const [phoneLoaded, setPhoneLoaded] = React.useState(false);
  const [innLoaded, setInnLoaded] = React.useState(false);
  const [fullNameLoaded, setFullNameLoaded] = React.useState(false);
  const [addressLoaded, setAddressLoaded] = React.useState(false);
  const [myDeliveries, setMyDeliveries] = React.useState([]);
  const [deliveriesLoaded, setDeliveriesLoaded] = React.useState(false);
  const [showAllDeliveries, setShowAllDeliveries] = React.useState(false);
  const [userCountry, setUserCountry] = React.useState('');
  const [customerCode, setCustomerCode] = React.useState(() => readCachedCustomerCode());

  const innDigits = digitsOnly(profile.inn).slice(0, 14);
  const phoneDigits = digitsOnly(profile.phone).slice(0, 12);
  const needsInn = !innDigits || innDigits.length < 12;
  const needsPhone = !phoneDigits;

  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      const initDataRaw = initData?.initData;
      if (!initDataRaw) return;

      const [profileResult, codeResult, deliveriesResult] = await Promise.allSettled([
        apiPost('/api/user/profile/get', { initData: initDataRaw }),
        apiPost('/api/user/customer-code/get', { initData: initDataRaw }),
        apiPost('/api/delivery/my', { initData: initDataRaw }),
      ]);

      if (!alive) return;

      const profileResp = profileResult.status === 'fulfilled' ? profileResult.value : null;
      const userData = profileResp?.user || {};
      const codeResp = codeResult.status === 'fulfilled' ? codeResult.value : null;

      const nextFullName = String(userData?.full_name || '').trim();
      const nextAddress = String(userData?.address || '').trim();
      const nextNickname = String(userData?.nickname || '').trim();
      const nextInn = digitsOnly(userData?.inn || '').slice(0, 14);
      const nextPhone = maskPhone(userData?.phone || '');
      const nextCountry = userData?.country || codeResp?.country || '';
      const nextCustomerCode = String(codeResp?.customerCode || userData?.customer_code || '').trim();

      setProfile((p) => ({
        ...p,
        fullName: nextFullName,
        address: nextAddress,
        nickname: nextNickname,
        inn: nextInn,
        phone: nextPhone,
      }));
      setUserCountry(nextCountry);
      setCustomerCode(nextCustomerCode);

      try {
        if (nextFullName) localStorage.setItem(LS_FULL_NAME_KEY, nextFullName);
        else localStorage.removeItem(LS_FULL_NAME_KEY);
        window.dispatchEvent(new Event('japanexpress:full-name-updated'));
      } catch (_) {}

      try {
        if (nextAddress) localStorage.setItem(LS_ADDRESS_KEY, nextAddress);
        else localStorage.removeItem(LS_ADDRESS_KEY);
        window.dispatchEvent(new Event('japanexpress:address-updated'));
      } catch (_) {}

      try {
        if (nextNickname) localStorage.setItem(LS_NICKNAME_KEY, nextNickname);
        else localStorage.removeItem(LS_NICKNAME_KEY);
      } catch (_) {}

      try {
        if (nextCustomerCode) localStorage.setItem(LS_CUSTOMER_CODE_KEY, nextCustomerCode);
        else localStorage.removeItem(LS_CUSTOMER_CODE_KEY);
      } catch (_) {}

      try {
        if (nextInn) localStorage.setItem(LS_INN_KEY, nextInn);
        else localStorage.removeItem(LS_INN_KEY);
        window.dispatchEvent(new Event('japanexpress:inn-updated'));
      } catch (_) {}

      try {
        const digits = digitsOnly(nextPhone).slice(0, 12);
        if (digits) localStorage.setItem(LS_PHONE_KEY, digits);
        else localStorage.removeItem(LS_PHONE_KEY);
        window.dispatchEvent(new Event('japanexpress:phone-updated'));
      } catch (_) {}

      const deliveriesResp = deliveriesResult.status === 'fulfilled' ? deliveriesResult.value : null;
      setMyDeliveries(Array.isArray(deliveriesResp?.deliveries) ? deliveriesResp.deliveries : []);
      setFullNameLoaded(true);
      setAddressLoaded(true);
      setInnLoaded(true);
      setPhoneLoaded(true);
      setDeliveriesLoaded(true);

    };

    run();
    return () => {
      alive = false;
    };
  }, [initData?.initData]);

  const displayName = user?.first_name || user?.last_name ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim() : '';
  const username = user?.username ? `@${user.username}` : '';

  const onSave = (e) => {
    e.preventDefault();
    const initDataRaw = initData?.initData;
    setError('');

    if (!innDigits || innDigits.length < 12) {
      setError('ИНН должен содержать минимум 12 цифр');
      return;
    }

    if (!phoneDigits || phoneDigits.length < 8) {
      setError('Введите номер телефона');
      return;
    }

    const trimmedName = String(profile.fullName || '').trim();
    const trimmedAddress = String(profile.address || '').trim();

    Promise.resolve()
      .then(async () => {
        if (initDataRaw) {
          await apiPost('/api/user/profile/update', {
            initData: initDataRaw,
            fullName: trimmedName,
            address: trimmedAddress,
            inn: innDigits,
            phone: phoneDigits,
            nickname: profile.nickname || '',
          });

        try {
          const nick = String(profile.nickname || '').trim();
          if (nick) localStorage.setItem(LS_NICKNAME_KEY, nick);
          else localStorage.removeItem(LS_NICKNAME_KEY);
        } catch (_) {}
        }

        try {
          if (trimmedName) localStorage.setItem(LS_FULL_NAME_KEY, trimmedName);
          else localStorage.removeItem(LS_FULL_NAME_KEY);
          window.dispatchEvent(new Event('japanexpress:full-name-updated'));
        } catch (_) {
          // ignore
        }

        try {
          if (trimmedAddress) localStorage.setItem(LS_ADDRESS_KEY, trimmedAddress);
          else localStorage.removeItem(LS_ADDRESS_KEY);
          window.dispatchEvent(new Event('japanexpress:address-updated'));
        } catch (_) {
          // ignore
        }

        saveProfile({
          fullName: profile.fullName,
          address: profile.address,
          notifyStatus: profile.notifyStatus,
          notifyPromo: profile.notifyPromo,
        });
        setSavedAt(new Date());
      })
      .catch((err) => {
        setError(err?.message || 'Ошибка сохранения');
      });
  };

  return (
    <div className="min-h-screen bg-bg text-text-primary pb-20">
      <div className="px-4 pt-6 max-w-2xl mx-auto">
        <h1 className="text-xl font-semibold tracking-tight">Профиль</h1>
        <p className="mt-2 text-sm text-text-secondary">Данные для связи и настройки уведомлений.</p>

        {innLoaded && needsInn ? (
          <div className="mt-4 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            Для оформления нужен ИНН. Укажите ИНН с паспорта.
          </div>
        ) : null}

        {phoneLoaded && needsPhone ? (
          <div className="mt-4 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            Для оформления и уведомлений по заказам нужен номер телефона. Укажите номер, который вы оставляли при оформлении заказа.
          </div>
        ) : null}

        <div className="mt-4 rounded-2xl border border-stroke bg-surface backdrop-blur-md p-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl border border-stroke bg-white/5 overflow-hidden flex items-center justify-center">
              {user?.photo_url ? (
                <img src={user.photo_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="h-full w-full bg-gradient-to-br from-[#3B82F6]/25 via-[#8A5CF6]/20 to-[#FACC15]/15" />
              )}
            </div>

            <div className="min-w-0">
              <div className="text-sm font-semibold text-text-primary truncate">
                {displayName || 'Пользователь'}
              </div>
              <div className="mt-0.5 text-xs text-text-secondary truncate">
                {username || (user?.id ? `ID: ${user.id}` : '')}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 rounded-2xl border border-neon-yellow/35 bg-neon-yellow/10 p-5 text-center">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-secondary">Ваш индивидуальный код</div>
          <div className="mt-2 font-mono text-3xl font-extrabold tracking-[0.18em] text-neon-yellow">
            {customerCode || 'Загрузка...'}
          </div>
          <div className="mt-2 text-xs text-text-secondary">Указывайте этот код при оформлении заказов на сторонних сервисах.</div>
        </div>

        <form onSubmit={onSave} className="mt-5 space-y-4">
          <div className="rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5 space-y-4">
            <Field label="ФИО">
              <Input
                value={profile.fullName}
                onChange={(e) => setProfile((p) => ({ ...p, fullName: cyrillicOnly(e.target.value) }))}
                placeholder="Иванов Иван Иванович"
                autoComplete="name"
              />
            </Field>

            <Field label="Адрес проживания">
              <Input
                value={profile.address}
                onChange={(e) => setProfile((p) => ({ ...p, address: e.target.value }))}
                placeholder="г. Бишкек, ул. Примерная, д. 1, кв. 2"
                autoComplete="street-address"
              />
            </Field>

            <Field label="Имя получателя (необязательно)">
              <Input
                value={profile.nickname || ''}
                onChange={(e) => setProfile((p) => ({ ...p, nickname: e.target.value }))}
                placeholder="Имя получателя"
                autoComplete="off"
              />
            </Field>

            <Field label="ИНН">
              <Input
                value={profile.inn}
                onChange={(e) => setProfile((p) => ({ ...p, inn: digitsOnly(e.target.value).slice(0, 14) }))}
                placeholder="12345678901234"
                inputMode="numeric"
                autoComplete="off"
              />
            </Field>

            <Field label="Телефон">
              <Input
                value={profile.phone}
                onChange={(e) => setProfile((p) => ({ ...p, phone: maskPhone(e.target.value) }))}
                placeholder="+996 555 123 456"
                autoComplete="tel"
                inputMode="tel"
              />
            </Field>

            {error ? <div className="text-xs text-red-300">{error}</div> : null}

            <button
              type="submit"
              className="w-full py-3 rounded-2xl font-semibold text-sm bg-gradient-to-r from-[#3B82F6] via-[#8A5CF6] to-[#FACC15] text-white hover:brightness-110 transition"
            >
              Сохранить
            </button>

            {savedAt && (
              <div className="text-xs text-text-muted text-center">
                Сохранено: {savedAt.toLocaleString()}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <Toggle
              checked={profile.notifyStatus}
              onChange={(v) => setProfile((p) => ({ ...p, notifyStatus: v }))}
              label="Уведомления по статусам"
              description="Сообщения, когда заказ поменял статус."
            />
          </div>
        </form>

        {/* Country selector */}
        <div className="mt-5 rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-1">Страна получения посылок</div>
          <div className="text-xs text-text-secondary mb-3">Влияет на доставку, валюту и реквизиты для оплаты</div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={async () => {
                if (userCountry === 'KG') return;
                const initDataRaw = initData?.initData;
                if (!initDataRaw) return;
                const ok = tg?.showPopup
                  ? await new Promise((resolve) => tg.showPopup({ title: 'Смена страны', message: 'Переключить на Кыргызстан? Это повлияет на доставку и валюту.', buttons: [{ id: 'cancel', type: 'cancel', text: 'Отмена' }, { id: 'ok', type: 'default', text: 'Да' }] }, (bid) => resolve(bid === 'ok')))
                  : window.confirm('Переключить на Кыргызстан?');
                if (!ok) return;
                try {
                  await apiPost('/api/user/country/set', { initData: initDataRaw, country: 'KG' });
                  setUserCountry('KG');
                  window.dispatchEvent(new CustomEvent('japanexpress:country-updated', { detail: { country: 'KG' } }));
                } catch (_) {}
              }}
              className={`flex-1 py-3 rounded-2xl font-semibold text-sm transition ${
                userCountry === 'KG'
                  ? 'bg-neon-blue/20 border-2 border-neon-blue/50 text-neon-blue shadow-[0_0_12px_rgba(59,130,246,0.2)]'
                  : 'border border-stroke bg-white/5 text-text-muted hover:bg-white/8'
              }`}
            >
              🇰🇬 Кыргызстан
            </button>
            <button
              type="button"
              onClick={async () => {
                if (userCountry === 'KZ') return;
                const initDataRaw = initData?.initData;
                if (!initDataRaw) return;
                const ok = tg?.showPopup
                  ? await new Promise((resolve) => tg.showPopup({ title: 'Смена страны', message: 'Переключить на Казахстан? Это повлияет на доставку и валюту.', buttons: [{ id: 'cancel', type: 'cancel', text: 'Отмена' }, { id: 'ok', type: 'default', text: 'Да' }] }, (bid) => resolve(bid === 'ok')))
                  : window.confirm('Переключить на Казахстан?');
                if (!ok) return;
                try {
                  await apiPost('/api/user/country/set', { initData: initDataRaw, country: 'KZ' });
                  setUserCountry('KZ');
                  window.dispatchEvent(new CustomEvent('japanexpress:country-updated', { detail: { country: 'KZ' } }));
                } catch (_) {}
              }}
              className={`flex-1 py-3 rounded-2xl font-semibold text-sm transition ${
                userCountry === 'KZ'
                  ? 'bg-neon-yellow/20 border-2 border-neon-yellow/50 text-neon-yellow shadow-[0_0_12px_rgba(250,204,21,0.2)]'
                  : 'border border-stroke bg-white/5 text-text-muted hover:bg-white/8'
              }`}
            >
              🇰🇿 Казахстан
            </button>
          </div>
          {userCountry ? (
            <div className="mt-2 text-[11px] text-text-muted text-center">
              Текущая: {userCountry === 'KZ' ? '🇰🇿 Казахстан' : '🇰🇬 Кыргызстан'}
            </div>
          ) : null}
        </div>

        {deliveriesLoaded && myDeliveries.length > 0 ? (
          <div className="mt-5 rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5">
            <div className="text-sm font-semibold mb-3">Мои доставки</div>
            <div className="space-y-2">
              {myDeliveries.slice(0, showAllDeliveries ? myDeliveries.length : 3).map((d) => {
                const statusMeta = {
                  'В обработке': { color: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/35', icon: '📦' },
                  'Ожидает': { color: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/35', icon: '📦' },
                  'Задерживается': { color: 'bg-orange-500/15 text-orange-300 border-orange-500/35', icon: '⏳' },
                  'Доставлено': { color: 'bg-green-500/15 text-green-300 border-green-500/35', icon: '✅' },
                  'Отменена': { color: 'bg-red-500/15 text-red-300 border-red-500/35', icon: '❌' },
                };
                const meta = statusMeta[d.status] || statusMeta['Ожидает'];
                return (
                  <div key={d.id} className="rounded-xl border border-stroke bg-white/3 p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-lg border ${meta.color}`}>
                        {meta.icon} {d.status}
                      </span>
                      <span className="text-[11px] text-text-muted">#{d.id}</span>
                    </div>
                    <div className="text-[11px] text-text-secondary space-y-0.5">
                      <div>{d.address || '—'}</div>
                      <div>Дата: {d.delivery_date || '—'}</div>
                    </div>
                  </div>
                );
              })}
              {myDeliveries.length > 3 && !showAllDeliveries ? (
                <button
                  type="button"
                  onClick={() => setShowAllDeliveries(true)}
                  className="w-full py-2 rounded-xl text-xs font-semibold border border-stroke bg-white/5 text-text-secondary hover:bg-white/8 transition"
                >
                  Показать все ({myDeliveries.length})
                </button>
              ) : null}
              {showAllDeliveries && myDeliveries.length > 3 ? (
                <button
                  type="button"
                  onClick={() => setShowAllDeliveries(false)}
                  className="w-full py-2 rounded-xl text-xs font-semibold border border-stroke bg-white/5 text-text-secondary hover:bg-white/8 transition"
                >
                  Свернуть
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ProfilePage;

